import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, MagnifyingGlass, UploadSimple, Handshake } from "@phosphor-icons/react";
import BulkReconciliationDialog from "@/components/BulkReconciliationDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, Kpi, Th, Td, Empty, Tag, Balance, Segmented } from "@/components/common";
import AccountDialog from "@/components/AccountDialog";
import AccountImportDialog from "@/components/ImportDialog";
import { api, money, L } from "@/lib/api";
import { useLoad, useQueryParam } from "@/lib/useLoad";

export default function Accounts() {
  const nav = useNavigate();
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  useQueryParam("tip", setType);
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [rows, reload] = useLoad(() => api.get("/accounts", { params: { type: type || undefined, q: q || undefined } }).then((r) => r.data), [type, q]);
  const list = rows || [];
  const rec = list.filter((a) => a.balance > 0).reduce((s, a) => s + a.balance, 0);
  const pay = list.filter((a) => a.balance < 0).reduce((s, a) => s - a.balance, 0);

  return (
    <div>
      <PageHeader eyebrow="Müşteri & Tedarikçi" title="Cari Hesaplar">
        <Button variant="outline" onClick={() => setBulkOpen(true)} className="rounded-[4px] border-[#2dd4bf] text-teal-800" data-testid="bulk-reconciliation-button"><Handshake weight="fill" /> Toplu Mutabakat</Button>
        <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-[4px]" data-testid="import-accounts-button"><UploadSimple /> Excel'den Aktar</Button>
        <Button onClick={() => setOpen(true)} className="rounded-[4px]" data-testid="new-account-button"><Plus /> Yeni Cari</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Cari Sayısı" value={list.length} testid="accounts-count" />
        <Kpi label="Toplam Alacağımız" value={money(rec)} tone="green" testid="accounts-receivable" delay={40} />
        <Kpi label="Toplam Borcumuz" value={money(pay)} tone="red" testid="accounts-payable" delay={80} />
      </div>
      <div className="panel">
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
          <Segmented value={type} onChange={setType} prefix="accounts-filter" options={[["", "Tümü"], ["customer", "Müşteriler"], ["supplier", "Tedarikçiler"]]} />
          <div className="relative w-full sm:w-72">
            <MagnifyingGlass className="absolute left-2.5 top-2.5 text-muted-foreground" size={16} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad veya kod ara…" className="pl-8" data-testid="accounts-search-input" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="accounts-table">
            <thead><tr><Th>Kod</Th><Th>Cari Adı</Th><Th>Tür</Th><Th>Telefon</Th><Th right>Borç</Th><Th right>Alacak</Th><Th right>Bakiye</Th></tr></thead>
            <tbody>
              {rows && list.length === 0 && <Empty cols={7} />}
              {list.map((a) => (
                <tr key={a.id} onClick={() => nav(`/cariler/${a.id}`)} className="cursor-pointer transition-colors hover:bg-muted/50" data-testid={`account-row-${a.id}`}>
                  <Td className="font-num text-xs text-muted-foreground">{a.code}</Td>
                  <Td className="font-medium">{a.name}</Td>
                  <Td><Tag tone={a.type === "supplier" ? "purchase" : "sale"}>{L.accType[a.type]}</Tag></Td>
                  <Td className="text-muted-foreground">{a.phone || "—"}</Td>
                  <Td right>{money(a.debit)}</Td>
                  <Td right>{money(a.credit)}</Td>
                  <Td right><Balance v={a.balance} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 text-[11px] text-muted-foreground">(B) Borçlu: cari bize borçlu · (A) Alacaklı: biz cariye borçluyuz</div>
      </div>
      <AccountDialog open={open} onOpenChange={setOpen} onSaved={(a) => { reload(); nav(`/cariler/${a.id}`); }} />
      <AccountImportDialog open={importOpen} onOpenChange={setImportOpen} onImported={reload} />
      <BulkReconciliationDialog open={bulkOpen} onOpenChange={setBulkOpen} />
    </div>
  );
}
